import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { useIdentity } from "../../containers/IdentityContainer";
import { useTokenMaker } from "../../hooks";
import { fetchAircraft } from "../../hooks/api";
import { useFAATailNumbers } from "../../hooks/useFAATailNumbers";
import { Tenant } from "../../types";
import {
  tailNumbersMatch,
  tailNumberStartsWith,
  tailNumberIncludes,
  stripHyphens,
} from "../../utils/tailNumberUtils";

const levenshteinDistance = (s: string, t: string) => {
  if (!s.length) return t.length;
  if (!t.length) return s.length;
  const arr = [];
  for (let i = 0; i <= t.length; i++) {
    arr[i] = [i];
    for (let j = 1; j <= s.length; j++) {
      arr[i][j] =
        i === 0
          ? j
          : Math.min(
              arr[i - 1][j] + 1,
              arr[i][j - 1] + 1,
              arr[i - 1][j - 1] + (s[j - 1] === t[i - 1] ? 0 : 1)
            );
    }
  }
  return arr[t.length][s.length];
};

type Props = {
  disableAutocomplete: boolean;
  defaultValue?: string;
  warning?: string;
  tenants?: Array<Tenant>;
  onInputChange?: (tailNumber: string) => void;
  onChange?: (tenant: Tenant) => void;
};

const RGX_VALID_REGISTRATIONS = /[A-Z]/;

export const SelectTailNumber: React.FC<Props> = ({
  disableAutocomplete,
  defaultValue = "",
  warning,
  tenants = [],
  onChange = () => {},
  onInputChange = () => {},
}) => {
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();
  const { airplxIdentity } = useIdentity();
  const { url: tokenMakerURL } = useTokenMaker();
  const [value, setValue] = React.useState<string>("");
  const [debouncedValue, setDebouncedValue] = React.useState<string>("");
  const [requestDialogOpen, setRequestDialogOpen] =
    React.useState<boolean>(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    React.useState<boolean>(false);
  const [tailNumberToRequest, setTailNumberToRequest] =
    React.useState<string>("");
  const [isFocused, setIsFocused] = React.useState<boolean>(false);

  // Debounce the value to synchronize filtering with FAA API data fetch (300ms)
  // This prevents the dropdown from "bouncing" when FAA data arrives
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, 300);

    return () => clearTimeout(timer);
  }, [value]);

  // Memoize the exclude list to prevent unnecessary re-renders of the hook
  const excludeTailNumbers = React.useMemo(
    () => tenants.map((t) => t.tail_number),
    [tenants]
  );

  // useFAATailNumbers is used to fetch FAA tail numbers. exclude the ones that are already in the tenants list
  // to avoid duplicates in the autocomplete list
  const { loading, tailNumbers } = useFAATailNumbers(value, excludeTailNumbers);

  const tenantsForAutocomplete = React.useMemo(() => {
    return [...tenants, ...tailNumbers].filter(
      (t) => t.tail_number?.length > 2
    );
  }, [tenants, tailNumbers]);

  // Memoize sorted options to prevent recalculation on every render
  // Uses debouncedValue to sync with FAA API data arrival
  const sortedOptions = React.useMemo(() => {
    // If no input, return alphabetically sorted list
    if (!debouncedValue || debouncedValue.length < 1) {
      return [...tenantsForAutocomplete].sort((a, b) =>
        a.tail_number.localeCompare(b.tail_number)
      );
    }

    const valueUpper = debouncedValue.toUpperCase();

    // Sort with prefix-aware scoring (hyphen-flexible)
    return [...tenantsForAutocomplete].sort((a, b) => {
      const aNum = a.tail_number.toUpperCase();
      const bNum = b.tail_number.toUpperCase();

      // Priority 1: Exact match (ignoring hyphens)
      const aExact = tailNumbersMatch(aNum, valueUpper) ? 0 : 1;
      const bExact = tailNumbersMatch(bNum, valueUpper) ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;

      // Priority 2: Starts with (prefix match, ignoring hyphens) - prioritizes C-AAC for "C" or "CAAC"
      const aStarts = tailNumberStartsWith(aNum, valueUpper) ? 0 : 1;
      const bStarts = tailNumberStartsWith(bNum, valueUpper) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;

      // Priority 3: Levenshtein distance for fuzzy matching (using normalized tail numbers)
      const aDist = levenshteinDistance(stripHyphens(valueUpper), stripHyphens(aNum));
      const bDist = levenshteinDistance(stripHyphens(valueUpper), stripHyphens(bNum));
      if (aDist !== bDist) return aDist - bDist;

      // Priority 4: Alphabetical
      return aNum.localeCompare(bNum);
    });
  }, [tenantsForAutocomplete, debouncedValue]);

  // Check if the current value is not found in registry
  // Uses debouncedValue to prevent premature banner appearance while FAA data loads
  // Also checks loading state to prevent banner flash during API fetch
  // Only shows banner when there are NO results at all (not just no exact match)
  const isNotInRegistry = React.useMemo(() => {
    if (!debouncedValue || debouncedValue.length < 2) return false;
    if (loading) return false; // Don't show banner while still loading FAA data
    // Check if any tail numbers in the list partially match the input
    const hasMatches = tenantsForAutocomplete.some((tenant) =>
      tailNumberIncludes(tenant.tail_number, debouncedValue)
    );
    return !hasMatches;
  }, [debouncedValue, loading, tenantsForAutocomplete]);

  // Handle aircraft request submission
  const handleAircraftRequest = async () => {
    try {
      // First, insert into database via PostgREST
      const { error: dbError } = await postgrest
        .from("tail_number_requests")
        .insert({
          tail_number: tailNumberToRequest,
          fbo_id: activeFBO?.id,
          requested_by: airplxIdentity.userid,
        });

      if (dbError) {
        console.error("Failed to save request to database:", dbError);
        throw new Error("Database insert failed");
      }

      // Then, send email notification (optional - don't fail if email service is down)
      try {
        const params = new URLSearchParams({
          fbo_name: activeFBO?.name || "Unknown FBO",
          user_email: airplxIdentity.email,
          make: "Unknown",
          model: "Unknown",
          tail_number: tailNumberToRequest,
          uniqueMods: "Tail number not in registry",
          timeframe: "ASAP",
        });

        await fetch(`${tokenMakerURL}/request_aircraft?${params.toString()}`);
      } catch (emailError) {
        // Email failed but request was saved to database - that's fine
        console.warn("Email notification failed, but request was saved:", emailError);
      }

      setRequestDialogOpen(false);
      setConfirmationDialogOpen(true);
    } catch (error) {
      console.error("Failed to submit aircraft request:", error);
      // TODO: Show error snackbar to user
    }
  };

  React.useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  // Memoize filter function to prevent recreation on every render
  const memoizedFilterOptions = React.useCallback(
    (options: Array<Tenant>, state) => {
      const q = state.inputValue;
      if (!q) return options; // Don't filter if no input
      return options.filter((o) => tailNumberIncludes(o.tail_number, q));
    },
    []
  );

  if (disableAutocomplete) {
    return (
      <>
        <Stack direction="column" sx={{ width: "100%" }}>
          <TextField
            inputProps={{ testid: "tail_number" }}
            fullWidth
            size="small"
            label="Tail #"
            onChange={(evt) => {
              let newInputValue = evt.target.value.toLocaleUpperCase();
              // Only auto-prepend "N" if user is typing numbers and there's actual content
              if (
                newInputValue &&
                !RGX_VALID_REGISTRATIONS.test(newInputValue)
              ) {
                // default to N since it is the most common
                newInputValue = "N" + newInputValue;
              }
              onInputChange(newInputValue);
              setValue(newInputValue);
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            value={value}
          />{" "}
          {isNotInRegistry && value.length >= 2 && isFocused && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                mt: 1,
                p: 1.5,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                minHeight: 48,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  flex: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Not in registry yet.
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                sx={{ flexShrink: 0 }}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur from firing
                  setTailNumberToRequest(value);
                  setRequestDialogOpen(true);
                }}
              >
                Request
              </Button>
            </Stack>
          )}
          <Typography variant="caption" color="error">
            {warning}
          </Typography>
        </Stack>

        {/* Aircraft Request Dialog */}
        <Dialog
          open={requestDialogOpen}
          onClose={() => setRequestDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>New Aircraft Request</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This aircraft isn't in our registry yet. Press 'Request' to submit
              it to our team.
            </DialogContentText>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                disabled
                label="Tail Number"
                value={tailNumberToRequest}
                fullWidth
              />
              <TextField
                disabled
                label="FBO"
                value={activeFBO.name}
                fullWidth
              />
              <TextField
                disabled
                label="Requested By"
                value={airplxIdentity.email}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAircraftRequest}
            >
              Request
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmationDialogOpen}
          onClose={() => setConfirmationDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>✈️ Aircraft Request Sent!</DialogTitle>
          <DialogContent>
            <DialogContentText>
              We've received your request for aircraft{" "}
              <strong>{tailNumberToRequest}</strong>. Our team will review and
              add it to the registry soon. You'll be notified once it's
              available.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => setConfirmationDialogOpen(false)}
            >
              Got it!
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Stack direction="column" sx={{ width: "100%" }}>
        <Autocomplete
          size="small"
          fullWidth
          freeSolo
          options={sortedOptions}
          getOptionLabel={(option: Tenant | string) => {
            if (typeof option === "string") {
              return option;
            }
            return option.tail_number;
          }}
          filterOptions={memoizedFilterOptions}
          ListboxProps={{
            style: {
              maxHeight: '40vh',
              overflow: 'auto',
            },
          }}
          slotProps={{
            popper: {
              sx: {
                zIndex: 10000, // Higher than dialog (9600)
              },
            },
          }}
          onInputChange={(event: any, newInputValue: string) => {
            newInputValue = newInputValue.toLocaleUpperCase();
            // Only auto-prepend "N" if user is typing numbers and there's actual content
            if (newInputValue && !RGX_VALID_REGISTRATIONS.test(newInputValue)) {
              // default to N since it is the most common
              newInputValue = "N" + newInputValue;
            }
            onInputChange(newInputValue);
            setValue(newInputValue);
          }}
          onChange={async (event: any, tenant: Tenant) => {
            if (!tenant?.aircraft && tenant?.aircraft_id) {
              // if the tenant has an aircraft_id but no aircraft, we need to fetch it
              const aircraft = await fetchAircraft(
                postgrest,
                tenant.aircraft_id,
                activeFBO
              );
              tenant.aircraft = aircraft;
            }
            if (tenant && !tenant?.type) {
              tenant.type = "transient"; // default type if not set
            }
            onChange(tenant);
          }}
          value={value}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Tail #"
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              inputProps={{
                testid: "tail_number",
                ...params.inputProps,
                onKeyDown: (e) => {
                  // i don't want user to ever hit enter in this form
                  if (e.key === "Enter") {
                    e.stopPropagation();
                  }
                },
              }}
            />
          )}
        />
        {isNotInRegistry && value.length >= 2 && isFocused && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              mt: 1,
              p: 1,
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              minHeight: 40,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
                mr: 1,
              }}
            >
              This aircraft isn't in our registry yet.
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{ flexShrink: 0 }}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur from firing
                setTailNumberToRequest(value);
                setRequestDialogOpen(true);
              }}
            >
              Request
            </Button>
          </Stack>
        )}
      </Stack>
      {/* Aircraft Request Dialog */}
      <Dialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Aircraft Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This aircraft isn't in our registry yet. Press 'Request' to submit
            it to our team.
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              disabled
              label="Tail Number"
              value={tailNumberToRequest}
              fullWidth
            />
            <TextField disabled label="FBO" value={activeFBO.name} fullWidth />
            <TextField
              disabled
              label="Requested By"
              value={airplxIdentity.email}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAircraftRequest}
          >
            Request
          </Button>
        </DialogActions>
      </Dialog>{" "}
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>✈️ Aircraft Request Sent!</DialogTitle>
        <DialogContent>
          <DialogContentText>
            We've received your request for aircraft{" "}
            <strong>{tailNumberToRequest}</strong>. Our team will review and add
            it to the registry soon. You'll be notified once it's available.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setConfirmationDialogOpen(false)}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
